import {Box, Button, Card, Stack} from '@mui/material';
import React, {Fragment, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {WellBornTaggingEnum} from 'main/analytics/gaTagsEnum';
import {makeGetExamsTypesLocation} from 'main/factories/usecases';
import {AlertInfo, AlertModal, IconIllustration} from 'presentation/components';
import {CallPhoneButton} from 'presentation/components/CallPhoneButton';
import {Loading} from 'presentation/components/Loading/Loading';
import {PhonesDrawer} from 'presentation/components/PhonesDrawer';
import {PhonesMenu} from 'presentation/components/PhonesMenu';
import {ChatRoomsEnum, CopySectionMaskEnum, OperationStateTypesEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useGoToRebooking} from 'presentation/hooks/useGoToRebooking';
import Icon from 'presentation/icons';

import {TypeLocalNameEnum} from 'domain/usecases';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {BookingTypeEnum, FlowEnum, ufNameMap} from 'presentation/pages/private/booking/constants';

import {
	useAppointmentNotifications,
	useCallChatRoom,
	useMediaQueries,
} from 'presentation/pages/private/hooks';
import {MY_APPOINTMENT_DETAILS_PATH} from 'presentation/modules/myAppointments/constants/appointmentDetailsPath';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {LocationType} from 'presentation/pages/private/store/locationStore/types';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {getFlowTypeByBookingType, getUserInfoFromToken} from 'presentation/utils';
import {formatGender} from 'presentation/utils/formatGender';
import {StyledCardActions, StyledCardContent} from '../styles';
import {AppointmentContent} from './AppointmentContent';
import {AppointmentsNotificationProps} from './types';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {UNITS_CHANNELS_CONTACTS} from 'presentation/modules/booking/shared/constants/unitsChannelsContacts';

const {
	showDetails,
	rebook,
	notice,
	callToChat,
	call,
	close,
	tryAgain,
	serverError,
	serverErrorTitle,
} = translations['pt-br'].commons;
const {rescheduleExamBlockedModal} = translations['pt-br'].privateFlow.myAppointments;
const {contractSuspendedModalTitle, contractSuspendedModalDescription} =
	translations['pt-br'].others.contractSuspendedErrorModal;
const {choosePhoneNumber} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;
const {beBornWellInfos} = translations['pt-br'].privateFlow.bookingFlow.beBornWellFlow;

export function AppointmentsNotifications({appointment}: AppointmentsNotificationProps) {
	const navigate = useNavigate();
	const [isBornWellActiveState, setIsBornWellActiveState] = useState<boolean>(false);
	const [openPhoneDrawerState, setOpenPhoneDrawerState] = useState<boolean>(false);
	const [anchorElState, setAnchorElState] = useState<null | HTMLElement>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isContractSuspendedModalActiveState, setIsContractSuspendedModalActiveState] =
		useState<boolean>(false);
	const [isExamBlockedModalActiveState, setIsExamBlockedModalActiveState] =
		useState<boolean>(false);
	const [operationState, setOperationState] = useState(OperationStateTypesEnum.NONE);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const flowType = getFlowTypeByBookingType(appointment.bookingType);
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const {isMobile} = useMediaQueries();
	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();
	const dataHostNameState = useHostName();
	const {accountData} = useAccountDataStore();

	const isExamsTypesLocationError = operationState === OperationStateTypesEnum.ERROR;

	const isPhoneNumberMenuOpen = Boolean(anchorElState);

	const handleOpenPhoneMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isMobile) {
			setOpenPhoneDrawerState(true);
			return;
		}
		setAnchorElState(event.currentTarget);
	};

	const handleTogglePhoneDrawer = () => {
		setOpenPhoneDrawerState(prev => !prev);
	};

	const handleClosePhoneMenu = () => {
		setAnchorElState(null);
	};

	const getLocationTypeFromFlow = (): LocationType =>
		isOdontoFlow ? FlowType.ODONTO : FlowType.DEFAULT;

	const locationType = getLocationTypeFromFlow();
	const getLocation = useLocationStore(state => state.getLocation);
	const currentAppointmentLocation = getLocation(cdPessoaC, locationType);

	const {nearestAppointment, isPastDue} = useAppointmentNotifications({
		appointment,
	});

	const {variant, iconName} = useBookingTheme({
		flowType,
	});

	const {formattedAddress, specialtyName, specialtyCode, subSpecialtyCode, bookingNumber} =
		appointment;

	const splittedAddress = formattedAddress.split(' - ');
	const [cityCode, ufCode] = splittedAddress[splittedAddress.length - 1].split('/');
	const ufName = ufNameMap[ufCode.toUpperCase()];

	const getBookingTypeAndOrigin = (): BookingOriginEnum => {
		if (appointment.bookingType === BookingTypeEnum.TELEHEALTH)
			return BookingOriginEnum.BOOKING_TELEHEALTH;
		if (appointment.bookingType === BookingTypeEnum.EXAM) return BookingOriginEnum.BOOKING_EXAM;
		if (appointment.bookingType === BookingTypeEnum.ODONTO)
			return BookingOriginEnum.BOOKING_ODONTO;
		return BookingOriginEnum.BOOKING_HEALTH;
	};

	const listPhoneNumbers =
		UNITS_CHANNELS_CONTACTS[dataHostNameState.dataHostNameState.product].phones;
	const listPhoneNumbersAlert =
		UNITS_CHANNELS_CONTACTS[dataHostNameState.dataHostNameState.product].alert;

	const handleRebookingExamFlow = async () => {
		try {
			setOperationState(OperationStateTypesEnum.SUBMITTING);

			const response = await makeGetExamsTypesLocation().get({
				stateCode: ufCode,
				cityName: cityCode.toUpperCase(),
				age: accountData.beneficiary.idadeC,
				gender: formatGender(accountData.beneficiary.flSexoUsuarioC),
				typeLocalName: TypeLocalNameEnum.CIDADE,
			});

			const examList = response?.tiposExames ?? [];
			const examBySpecialtyCode = examList.find(item => item?.cdTipoExame === specialtyCode);

			if (!examBySpecialtyCode) {
				setIsExamBlockedModalActiveState(true);
				setOperationState(OperationStateTypesEnum.DONE);
				return;
			}

			setOperationState(OperationStateTypesEnum.DONE);
		} catch {
			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const {goToRebooking} = useGoToRebooking({
		specialtyName,
		specialtyCode,
		subSpecialtyCode: subSpecialtyCode || '',
		bookingNumber,
		bookingType: appointment.bookingType,
		ufCode,
		cityCode,
		ufName,
		email: appointment.email,
		phone: appointment.phone,
		flowType,
		isTelehealth: flowType === FlowEnum.TELEHEALTH,
		currentAppointmentLocation,
		locationType,
		setIsBornWellActiveState,
		setIsContractSuspendedModalActiveState,
		getBookingTypeAndOrigin,
		setActiveBookingFlowStep,
	});

	const actionBtnText = isPastDue ? rebook : showDetails;

	function handleShowDetails() {
		const path = MY_APPOINTMENT_DETAILS_PATH[appointment.bookingType];
		const id = nearestAppointment.bookingNumber;

		navigate(path.replace(':id', String(id)));
	}

	async function handleAction() {
		if (isLoading) return;

		setIsLoading(true);

		try {
			if (isPastDue) {
				await goToRebooking();
			} else {
				handleShowDetails();
			}
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Fragment>
			<Card elevation={0}>
				<StyledCardContent>
					<AppointmentContent appointment={appointment} />
					<IconIllustration variant={variant} size="small" customIconName={iconName} />
				</StyledCardContent>
				<StyledCardActions>
					<Button
						onClick={handleAction}
						variant="text"
						disabled={isLoading}
						endIcon={
							isLoading ? (
								<Loading color="inherit" size={16} />
							) : (
								<Icon name="arrow-right-filled" />
							)
						}>
						{actionBtnText}
					</Button>
				</StyledCardActions>
			</Card>

			<AlertModal
				open={isContractSuspendedModalActiveState}
				onClose={() => setIsContractSuspendedModalActiveState(false)}
				description={contractSuspendedModalDescription.line1}
				subDescription={contractSuspendedModalDescription.line2}
				title={contractSuspendedModalTitle}
				headerTitle={notice}
				variant="info"
				footerContent={
					<Button
						variant="contained"
						size="large"
						fullWidth
						onClick={() => setIsContractSuspendedModalActiveState(false)}>
						{close}
					</Button>
				}
			/>

			<AlertModal
				open={isExamBlockedModalActiveState || isBornWellActiveState}
				onClose={() => {
					setIsExamBlockedModalActiveState(false);
					setIsBornWellActiveState(false);
				}}
				variant="info"
				title={rescheduleExamBlockedModal.title}
				headerTitle={notice}
				description={rescheduleExamBlockedModal.description}
				fullScreen={isMobile}
				fullWidth={isMobile}
				slotProps={{paper: isMobile ? {} : {style: {maxWidth: 420}}}}
				footerContent={
					<Stack gap={1} width={'100%'}>
						<Button
							size="large"
							fullWidth
							data-tag-id={
								isBornWellActiveState
									? WellBornTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_NASCER_BEM_REMARCACAO
									: undefined
							}
							onClick={async () => {
								if (isBornWellActiveState) {
									await callChatRoom(ChatRoomsEnum.BE_BORN_WELL);
									return;
								}
								await callChatRoom(ChatRoomsEnum.APP);
							}}
							endIcon={
								callChatRoomIsLoading ? <Loading size={16} color="inherit" /> : null
							}
							disabled={callChatRoomIsLoading}>
							{callToChat}
						</Button>
						{isBornWellActiveState ? (
							<Button
								variant={'outlined'}
								size="large"
								data-tag-id={
									WellBornTaggingEnum.CLICK_EXIBIR_TELEFONES_NASCER_BEM_REMARCACAO
								}
								endIcon={<Icon name="phone-filled" />}
								onClick={handleOpenPhoneMenu}>
								{call}
							</Button>
						) : (
							<CallPhoneButton
								phoneNumbers={listPhoneNumbers}
								phoneNumbersAlert={listPhoneNumbersAlert}
							/>
						)}
					</Stack>
				}
			/>
			{isMobile ? (
				<PhonesDrawer.Root
					open={openPhoneDrawerState}
					toggleDrawer={handleTogglePhoneDrawer}
					title={choosePhoneNumber}>
					<AlertInfo.Root>{[beBornWellInfos.alert]}</AlertInfo.Root>
					<PhonesDrawer.Simple
						mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
						phoneNumbers={[beBornWellInfos.telephoneNumber]}
					/>
				</PhonesDrawer.Root>
			) : (
				<PhonesMenu.Root
					open={isPhoneNumberMenuOpen}
					onClose={handleClosePhoneMenu}
					anchorEl={anchorElState}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}>
					<Box pb={2}>
						<AlertInfo.Root>{[beBornWellInfos.alert]}</AlertInfo.Root>
					</Box>
					<PhonesDrawer.Simple
						mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
						phoneNumbers={[beBornWellInfos.telephoneNumber]}
					/>
				</PhonesMenu.Root>
			)}

			<AlertModal
				open={isExamsTypesLocationError}
				onClose={() => setOperationState(OperationStateTypesEnum.NONE)}
				description={serverError}
				title={serverErrorTitle}
				headerTitle={notice}
				variant="info"
				footerContent={
					<Button
						variant="contained"
						size="large"
						fullWidth
						onClick={handleRebookingExamFlow}>
						{tryAgain}
					</Button>
				}
			/>
		</Fragment>
	);
}
